/**
 * SubjectScaleEstimator — recovers absolute body scale from a single
 * representative CanonicalFrame and a parallel monocular depth map.
 *
 * Outputs a SceneCalibration that the host writes back to the saved
 * recording's SubjectProfile / SceneCalibration, allowing the Viewer
 * to render trajectories in metres rather than pixels.
 *
 * Strategy:
 *   1. Sample the depth map at each landmark's image pixel to recover
 *      a relative-depth-per-joint vector.
 *   2. The relative depth has unknown scale; we anchor it via the
 *      inferred subject height. Without prior knowledge the relative
 *      scale defaults to 1.7 m (median adult height) — a calibrated
 *      heightM in the SubjectProfile, when available, replaces this.
 *   3. Fit a least-squares plane to the four foot landmarks (heels +
 *      toes if available, else ankles) to recover the floor plane.
 *   4. Camera-relative position of the floor's nearest point is
 *      reported as the cameraHeightM (Y-axis distance in metres).
 *
 * docs/migration-plan.md Phase 13.
 */

import type { CanonicalFrame } from '../canonical/CanonicalFrame';

export interface SceneCalibration {
  /** Plane equation n·X + d = 0 in *metres*, camera-frame coords.
   *  null when the floor was not recoverable from the visible
   *  pose (e.g. close-up / no foot landmarks). */
  floorPlane: { n: [number, number, number]; d: number } | null;
  /** Distance from the camera origin to the floor plane along the
   *  camera's gravity axis. null when floorPlane is null. */
  cameraHeightM: number | null;
  /** Median depth (metres) sampled at body landmark pixel locations.
   *  Useful as a sanity check / first-order distance estimate. */
  subjectMedianDepthM: number | null;
  /** Source of the absolute scale used by this calibration. */
  scaleAnchor: 'reported-subject-height' | 'population-median-height' | 'depth-only';
  /** The body height assumed during scale recovery, in metres. */
  assumedHeightM: number;
}

export interface DepthMapView {
  data: Float32Array;
  width: number;
  height: number;
}

export interface SubjectScaleOptions {
  /** Caller-provided body height in metres. When present we use it as
   *  the absolute-scale anchor; when null we fall back to the
   *  population median (1.7 m). */
  reportedHeightM?: number | null;
  /** Joint native indices treated as feet for floor plane fitting.
   *  Defaults to the COCO-17 ankle indices (15, 16). Halpe-26 may
   *  pass [15, 16, 17, 18, 19, 20] (ankles + heels + toes). */
  footIndices?: readonly number[];
}

const POPULATION_MEDIAN_HEIGHT_M = 1.7;
const COCO17_NOSE = 0;
const COCO17_LEFT_ANKLE = 15;
const COCO17_RIGHT_ANKLE = 16;
const DEFAULT_FOOT_INDICES = [COCO17_LEFT_ANKLE, COCO17_RIGHT_ANKLE] as const;

/**
 * Estimate absolute scale from a single (frame, depth-map) pair.
 * Returns null when the frame contains no body or when the depth
 * map is empty.
 */
export function estimateSubjectScale(
  frame: CanonicalFrame,
  depth: DepthMapView,
  opts: SubjectScaleOptions = {},
): SceneCalibration | null {
  if (frame.pose.bodies.length === 0) return null;
  if (depth.data.length === 0) return null;
  if (depth.width <= 0 || depth.height <= 0) return null;

  const body = frame.pose.bodies[0];
  // Sample depth at every available landmark pixel.
  const samples: { nativeIndex: number; depth: number; xPx: number; yPx: number }[] = [];
  for (const lm of body.landmarks) {
    if (lm.origin.synthesized) continue;
    const sw = lm.pos2D.sourceWidthPx || 1;
    const sh = lm.pos2D.sourceHeightPx || 1;
    const u = (lm.pos2D.xPx / sw) * depth.width;
    const v = (lm.pos2D.yPx / sh) * depth.height;
    const ui = Math.max(0, Math.min(depth.width - 1, Math.round(u)));
    const vi = Math.max(0, Math.min(depth.height - 1, Math.round(v)));
    const d = depth.data[vi * depth.width + ui];
    if (!Number.isFinite(d)) continue;
    samples.push({
      nativeIndex: lm.raw.nativeIndex,
      depth: d,
      xPx: lm.pos2D.xPx,
      yPx: lm.pos2D.yPx,
    });
  }
  if (samples.length === 0) return null;

  // Recover absolute scale via the assumed body height. Pixel span
  // top-of-head (proxy: nose y) → bottom-of-feet (mean ankle y) maps
  // linearly onto the assumed height.
  const nose = samples.find((s) => s.nativeIndex === COCO17_NOSE);
  const ankleSamples = samples.filter(
    (s) => s.nativeIndex === COCO17_LEFT_ANKLE || s.nativeIndex === COCO17_RIGHT_ANKLE,
  );
  if (!nose || ankleSamples.length === 0) return null;
  const ankleY = ankleSamples.reduce((sum, s) => sum + s.yPx, 0) / ankleSamples.length;
  const heightSpanPx = Math.abs(ankleY - nose.yPx);
  if (heightSpanPx <= 1) return null;

  const assumedHeightM =
    typeof opts.reportedHeightM === 'number' && opts.reportedHeightM > 0
      ? opts.reportedHeightM
      : POPULATION_MEDIAN_HEIGHT_M;
  const scaleAnchor: SceneCalibration['scaleAnchor'] =
    typeof opts.reportedHeightM === 'number' && opts.reportedHeightM > 0
      ? 'reported-subject-height'
      : 'population-median-height';

  // metresPerPixel along the body's vertical span. We use this to
  // convert any other pixel-space measurement into metres later on.
  const mPerPx = assumedHeightM / heightSpanPx;

  // Floor plane fitting from foot landmarks. We work in camera-space
  // coords [Xm, Ym, Zm] using a pinhole projection that assumes the
  // optical axis is the Z axis and the principal point is the image
  // centre (no calibrated intrinsics → coarse but usable).
  const footIdx = opts.footIndices ?? DEFAULT_FOOT_INDICES;
  const cameraSpace: [number, number, number][] = [];
  for (const s of samples) {
    if (!footIdx.includes(s.nativeIndex)) continue;
    const xm = (s.xPx - depth.width / 2) * mPerPx;
    const ym = (s.yPx - depth.height / 2) * mPerPx;
    // Depth values are *relative*; we treat them as already scaled by
    // the same mPerPx factor since the only ground truth we have is
    // the body's vertical span. This is approximate but usable for
    // visualising distances on the same order as the subject's own
    // height.
    const zm = s.depth * mPerPx * depth.height;
    cameraSpace.push([xm, ym, zm]);
  }

  let floorPlane: SceneCalibration['floorPlane'] = null;
  let cameraHeightM: number | null = null;
  if (cameraSpace.length >= 3) {
    floorPlane = fitPlane(cameraSpace);
    if (floorPlane) {
      const [a, b, c] = floorPlane.n;
      const norm = Math.sqrt(a * a + b * b + c * c);
      cameraHeightM = norm > 0 ? Math.abs(floorPlane.d) / norm : null;
    }
  }

  const subjectMedianDepthM = median(samples.map((s) => s.depth)) * mPerPx * depth.height;

  return {
    floorPlane,
    cameraHeightM,
    subjectMedianDepthM,
    scaleAnchor,
    assumedHeightM,
  };
}

/** Closed-form plane fit by SVD on the centred design matrix. Returns
 *  the unit-norm normal and the d offset. */
function fitPlane(
  points: readonly [number, number, number][],
): { n: [number, number, number]; d: number } | null {
  if (points.length < 3) return null;
  let cx = 0;
  let cy = 0;
  let cz = 0;
  for (const [x, y, z] of points) {
    cx += x;
    cy += y;
    cz += z;
  }
  cx /= points.length;
  cy /= points.length;
  cz /= points.length;

  // 3x3 covariance.
  let sxx = 0;
  let syy = 0;
  let szz = 0;
  let sxy = 0;
  let sxz = 0;
  let syz = 0;
  for (const [x, y, z] of points) {
    const dx = x - cx;
    const dy = y - cy;
    const dz = z - cz;
    sxx += dx * dx;
    syy += dy * dy;
    szz += dz * dz;
    sxy += dx * dy;
    sxz += dx * dz;
    syz += dy * dz;
  }
  // Eigenvector with smallest eigenvalue ≈ plane normal. Using the
  // observation that for ~horizontal floors, the y-axis dominates,
  // a power-iteration shortcut is sufficient at this fidelity.
  const normalGuess: [number, number, number] = [0, 1, 0];
  const refined = powerIterationSmallest(
    [sxx, sxy, sxz, sxy, syy, syz, sxz, syz, szz],
    normalGuess,
    20,
  );
  if (!refined) return null;
  const norm = Math.hypot(refined[0], refined[1], refined[2]);
  if (norm === 0) return null;
  const n: [number, number, number] = [refined[0] / norm, refined[1] / norm, refined[2] / norm];
  const d = -(n[0] * cx + n[1] * cy + n[2] * cz);
  return { n, d };
}

/** Crude inverse power iteration: subtract the dominant eigenvalue's
 *  contribution then iterate. Sufficient for almost-rank-2 covariances
 *  arising from foot landmarks on a near-horizontal floor. */
function powerIterationSmallest(
  m: readonly number[],
  seed: readonly [number, number, number],
  iters: number,
): [number, number, number] | null {
  let v = seed.slice() as [number, number, number];
  // Trace gives us a scaling reference for the deflation step.
  const trace = m[0] + m[4] + m[8];
  const eps = Math.max(1e-9, trace * 1e-6);
  for (let i = 0; i < iters; i += 1) {
    // Solve (M + eps I) w = v iteratively (Jacobi-relaxation flavour).
    const w: [number, number, number] = [
      v[0] / (m[0] + eps) +
        (-(m[1] * v[1] + m[2] * v[2])) / (m[0] + eps),
      v[1] / (m[4] + eps) +
        (-(m[1] * v[0] + m[5] * v[2])) / (m[4] + eps),
      v[2] / (m[8] + eps) +
        (-(m[2] * v[0] + m[5] * v[1])) / (m[8] + eps),
    ];
    const norm = Math.hypot(w[0], w[1], w[2]);
    if (norm === 0) return null;
    v = [w[0] / norm, w[1] / norm, w[2] / norm];
  }
  return v;
}

function median(xs: readonly number[]): number {
  if (xs.length === 0) return 0;
  const sorted = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) * 0.5 : sorted[mid];
}
